import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Search, Filter, CheckCircle, XCircle, AlertCircle, Edit, Eye, FileText, Users, CalendarDays, ChevronDown, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from '@/components/ui/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import SubmissionDetailModal from './components/SubmissionDetailModal';
import ContactSubmitterModal from './components/ContactSubmitterModal';
import { submissionsService } from '@/services/submissionsService';
import { getImageUrl } from '@/services/api';

const SubmissionsReviewPage = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [availableYears, setAvailableYears] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [decisionData, setDecisionData] = useState({ 
    decision: '', 
    score: '', 
    comments: '', 
    conditions: '',
    reviewer_name: ''
  });

  useEffect(() => {
    loadSubmissions();
  }, [filterStatus, activeTab, filterYear]);

  useEffect(() => {
    submissionsService.getAvailableYears().then(setAvailableYears).catch(() => setAvailableYears([]));
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const filters = {};
      
      if (filterStatus !== 'all') {
        filters.status = filterStatus;
      }
      
      if (filterYear !== 'all') {
        filters.year = filterYear;
      }
      
      if (searchTerm) {
        filters.search = searchTerm;
      }
      
      const response = await submissionsService.getAllSubmissions(filters);
      
      if (response.success) {
        setSubmissions(response.data || []);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast({
        title: "เกิดข้อผิดพลาด",
        description: "ไม่สามารถโหลดข้อมูลได้",
        variant: "destructive"
      });
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDecisionModal = (submission) => {
    setSelectedSubmission(submission);
    setDecisionData({ 
      decision: submission.review_status || submission.decision || '', 
      score: submission.review_score || submission.score || '', 
      comments: submission.review_comments || submission.comments || '',
      conditions: submission.conditions || '',
      reviewer_name: submission.reviewer_name || submission.reviewer || ''
    });
    setIsDecisionModalOpen(true);
  };

  const handleDecisionSubmit = async () => {
    if (!selectedSubmission || !decisionData.decision) {
      toast({ 
        title: "ข้อมูลไม่ครบถ้วน", 
        description: "กรุณาเลือกผลการตัดสิน", 
        variant: "destructive" 
      });
      return;
    }

    try {
      const updateData = {
        review_status: decisionData.decision,
        review_score: decisionData.score ? parseInt(decisionData.score) : null,
        review_comments: decisionData.comments,
        reviewer_name: decisionData.reviewer_name || null,
        conditions: decisionData.conditions,
        status: 'reviewed'
      };

      const response = await submissionsService.updateSubmissionReview(
        selectedSubmission.id,
        updateData
      );

      if (response.success) {
        toast({ 
          title: "บันทึกผลการตัดสินสำเร็จ", 
          description: `ผลงาน "${selectedSubmission.title || selectedSubmission.work_title}" ได้รับการประเมินแล้ว` 
        });
        setIsDecisionModalOpen(false);
        setSelectedSubmission(null);
        loadSubmissions();
      }
    } catch (error) {
      console.error('Error saving decision:', error);
      toast({
        title: "เกิดข้อผิดพลาด",
        description: "ไม่สามารถบันทึกข้อมูลได้",
        variant: "destructive"
      });
    }
  };

  const handleViewDetails = (submission) => {
    setSelectedSubmission(submission);
    setIsDetailModalOpen(true);
  };

  const handleDownloadFile = (submission) => {
    const pdfUrl = submission.research_file_url;
    if (!pdfUrl) {
      toast({ title: "ไม่มีไฟล์", description: "ผลงานนี้ยังไม่มีไฟล์เอกสาร", variant: "destructive" });
      return;
    }
    const fullUrl = getImageUrl(pdfUrl) || (pdfUrl.startsWith('http') ? pdfUrl : pdfUrl);
    const a = document.createElement('a');
    a.href = fullUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.download = `${submission.project_title || submission.title || 'document'}_research.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast({ title: "เริ่มดาวน์โหลด", description: `กำลังดาวน์โหลดไฟล์ "${submission.project_title || submission.title || 'เอกสาร'}"` });
  };

  const handleContactSubmitter = (submission) => {
    setSelectedSubmission(submission);
    setIsContactModalOpen(true);
  };

  const handleContactSubmit = () => {
    setIsContactModalOpen(false);
    toast({ 
      title: "ส่งข้อความสำเร็จ!", 
      description: `ข้อความของคุณถูกส่งไปยัง ${selectedSubmission?.first_name} ${selectedSubmission?.last_name} แล้ว` 
    });
  };

  const handleDeleteClick = (sub) => {
    setDeleteTarget(sub);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const res = await submissionsService.deleteSubmission(deleteTarget.id);
      if (res.success) {
        toast({ title: "ลบสำเร็จ", description: "ผลงานถูกลบแล้ว" });
        setDeleteTarget(null);
        loadSubmissions();
      } else {
        toast({
          title: "เกิดข้อผิดพลาด",
          description: res.message || "ไม่สามารถลบผลงานได้",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "เกิดข้อผิดพลาด",
        description: error.message || "ไม่สามารถลบผลงานได้",
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleFeatureClick = (feature) => {
    toast({
      title: `🚧 ฟีเจอร์ "${feature}" ยังไม่ได้พัฒนา`,
      description: "แต่ไม่ต้องกังวล! ฟีเจอร์นี้จะใช้งานได้ในเร็วๆนี้  🚀",
    });
  };

  // Helper function to get category from submission
  const getSubmissionCategory = (sub) => {
    // Try all possible fields
    const cat = sub.category || sub.work_category || sub.type || sub.work_type || 
                sub.registration_type || '';
    return String(cat || '').trim();
  };

  // Helper function to check if submission is academic work
  const isAcademicWork = (sub) => {
    const cat = getSubmissionCategory(sub);
    if (!cat) return false;
    const catLower = cat.toLowerCase();
    
    // Check for research/academic types
    return cat === 'research' ||           // ✅ ตรงกับ DB
           cat === 'academic' || 
           cat === 'ผลงานวิชาการ' ||
           catLower.includes('research') ||
           catLower.includes('academic') ||
           catLower.includes('วิชาการ') ||
           catLower.includes('paper');
  };

  // Helper function to check if submission is creative work
  const isCreativeWork = (sub) => {
    const cat = getSubmissionCategory(sub);
    if (!cat) return false;
    const catLower = cat.toLowerCase();
    
    // Check for general/creative types
    return cat === 'general' ||            // ✅ ตรงกับ DB
           cat === 'creative' || 
           cat === 'ผลงานสร้างสรรค์' ||
           catLower.includes('general') ||
           catLower.includes('creative') ||
           catLower.includes('สร้างสรรค์') ||
           catLower.includes('art') ||
           catLower.includes('craft');
  };

  const filteredSubmissions = submissions.filter(sub => {
    const searchTermLower = searchTerm.toLowerCase();
    const title = sub.title || sub.work_title || sub.name || sub.work_name || 
                  sub.submission_title || sub.project_title || '';
    const author = `${sub.first_name || ''} ${sub.last_name || ''}`.trim();
    const eventName = sub.event_name || sub.eventName || '';
    
    const matchesSearch = title.toLowerCase().includes(searchTermLower) ||
                         author.toLowerCase().includes(searchTermLower) ||
                         eventName.toLowerCase().includes(searchTermLower) ||
                         (sub.email && sub.email.toLowerCase().includes(searchTermLower));

    const matchesTab = activeTab === 'all' || 
      (activeTab === 'ผลงานวิชาการ' && isAcademicWork(sub)) ||
      (activeTab === 'ผลงานสร้างสรรค์' && isCreativeWork(sub));

    const matchesStatus = filterStatus === 'all' || 
                          (filterStatus === 'pending' && (sub.status === 'pending' || !sub.review_status)) ||
                          (filterStatus === 'reviewed' && sub.status === 'reviewed');

    return matchesSearch && matchesTab && matchesStatus;
  });

  const tabs = [
    { id: 'all', label: 'ทั้งหมด' },
    { id: 'ผลงานวิชาการ', label: 'ผลงานวิชาการ' },
    { id: 'ผลงานสร้างสรรค์', label: 'ผลงานสร้างสรรค์' },
  ];

  const statusFilters = [
    { id: 'all', label: 'ทุกสถานะ' },
    { id: 'pending', label: 'รอการประเมิน' },
    { id: 'reviewed', label: 'ตรวจสอบแล้ว' },
  ];

  const getStatusBadge = (status, decision) => {
    const reviewStatus = decision || status?.review_status;
    
    if (status === 'pending' || !reviewStatus) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertCircle className="w-3 h-3 mr-1.5"/>
          รอการประเมิน
        </span>
      );
    }
    
    if (reviewStatus === 'accepted') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1.5"/>
          ตอบรับ
        </span>
      );
    }
    
    if (reviewStatus === 'rejected') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1.5"/>
          ปฏิเสธ
        </span>
      );
    }
    
    if (reviewStatus === 'conditional_accept') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <AlertCircle className="w-3 h-3 mr-1.5"/>
          ตอบรับแบบมีเงื่อนไข
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        ตรวจสอบแล้ว
      </span>
    );
  };

  // Debug: Log first submission to see available fields
  useEffect(() => {
    if (submissions.length > 0) {
      console.log('📊 First submission data:', submissions[0]);
      console.log('📊 Title fields check:', submissions.map(s => ({
        id: s.id,
        title: s.title,
        work_title: s.work_title,
        name: s.name,
        work_name: s.work_name,
        submission_title: s.submission_title,
        first_name: s.first_name,
        last_name: s.last_name
      })));
      console.log('📊 All submission categories:', submissions.map(s => ({
        id: s.id,
        category: s.category,
        type: s.type,
        work_category: s.work_category,
        registration_type: s.registration_type,
        work_type: s.work_type,
        detected_category: getSubmissionCategory(s),
        is_academic: isAcademicWork(s),
        is_creative: isCreativeWork(s)
      })));
      
      // Count by category
      const academicCount = submissions.filter(isAcademicWork).length;
      const creativeCount = submissions.filter(isCreativeWork).length;
      console.log('📊 Category counts:', {
        total: submissions.length,
        academic: academicCount,
        creative: creativeCount,
        unknown: submissions.length - academicCount - creativeCount
      });
    }
  }, [submissions]);

  const tabCounts = {
    all: submissions.length,
    'ผลงานวิชาการ': submissions.filter(isAcademicWork).length,
    'ผลงานสร้างสรรค์': submissions.filter(isCreativeWork).length
  };

  return (
    <>
      <Helmet>
        <title>ตรวจสอบผลงาน - ระบบจัดการ SACIT</title>
        <meta name="description" content="ประเมินและตัดสินผลงานวิชาการและผลงานสร้างสรรค์" />
      </Helmet>
      
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-800">ตรวจสอบผลงาน</h1>
            <p className="text-gray-600 mt-1">ประเมินและตัดสินผลงานวิชาการและผลงานสร้างสรรค์</p>
          </div>
          <Button 
            className="add-button-gradient w-full sm:w-auto"
            onClick={() => handleFeatureClick('ประกาศผล')}
          >
            ประกาศผลการตัดสิน
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white p-4 sm:p-6 rounded-xl shadow-lg"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="ค้นหาชื่อผลงาน, ผู้ส่ง, ชื่องาน..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 w-full bg-gray-50 border-gray-300 focus:border-violet-500 text-sm"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full bg-gray-50 border-gray-300 text-sm">
                <SelectValue placeholder="ตัวกรองสถานะ" />
              </SelectTrigger>
              <SelectContent>
                {statusFilters.map(status => (
                  <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-full bg-gray-50 border-gray-300 text-sm">
                <SelectValue placeholder="ปี" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุกปี</SelectItem>
                {availableYears.map((y) => (
                  <SelectItem key={y} value={y}>ปี {y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4 border-b border-gray-200">
            <nav className="-mb-px flex space-x-2 sm:space-x-4 overflow-x-auto" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex-shrink-0
                    ${activeTab === tab.id
                      ? 'border-violet-600 text-violet-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.label} ({tabCounts[tab.id] || 0})
                </button>
              ))}
            </nav>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1000px]">
                  <thead>
                    <tr>
                      <th className="table-header-custom w-10">
                        <input type="checkbox" className="form-checkbox rounded text-violet-600 focus:ring-violet-500" />
                      </th>
                      <th className="table-header-custom">ชื่อผลงาน</th>
                      <th className="table-header-custom">ผู้ส่งผลงาน</th>
                      <th className="table-header-custom">ประเภท</th>
                      <th className="table-header-custom">วันที่ส่ง</th>
                      <th className="table-header-custom">สถานะ</th>
                      <th className="table-header-custom">ผู้ประเมิน</th>
                      <th className="table-header-custom text-center">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredSubmissions.map((sub) => {
                      const title = sub.title || sub.work_title || sub.name || sub.work_name || 
                                    sub.submission_title || sub.project_title || 
                                    `ผลงานของ ${sub.first_name || ''} ${sub.last_name || ''}`.trim() || 
                                    'ไม่ระบุชื่อผลงาน';
                      const author = `${sub.first_name || ''} ${sub.last_name || ''}`.trim() || 'ไม่ระบุ';
                      const getDisplayType = (sub) => {
                        const cat = getSubmissionCategory(sub);
                        
                        // Map registration_type to display name
                        if (cat === 'research') return 'ผลงานวิชาการ';
                        if (cat === 'general') return 'ผลงานสร้างสรรค์';
                        
                        if (isAcademicWork(sub)) return 'ผลงานวิชาการ';
                        if (isCreativeWork(sub)) return 'ผลงานสร้างสรรค์';
                        
                        return cat || 'ผลงานวิชาการ';
                      };
                      const type = getDisplayType(sub);
                      const eventName = sub.event_name || sub.eventName || 'SACIT Conference 2025';
                      const submissionDate = sub.created_at 
                        ? new Date(sub.created_at).toLocaleDateString('th-TH')
                        : sub.submissionDate || '-';
                      const reviewer = sub.reviewer_name || sub.reviewer || '-';
                      
                      return (
                        <motion.tr 
                          key={sub.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="table-cell-custom">
                            <input type="checkbox" className="form-checkbox rounded text-violet-600 focus:ring-violet-500" />
                          </td>
                          <td className="table-cell-custom font-medium text-gray-800">
                            <div className="max-w-xs truncate" title={title}>{title}</div>
                            <div className="text-xs text-gray-500 flex items-center mt-0.5">
                              <CalendarDays className="w-3 h-3 mr-1 text-gray-400"/> {eventName}
                            </div>
                          </td>
                          <td className="table-cell-custom">{author}</td>
                          <td className="table-cell-custom">{type}</td>
                          <td className="table-cell-custom">{submissionDate}</td>
                          <td className="table-cell-custom">
                            {getStatusBadge(sub.status, sub.review_status)}
                          </td>
                          <td className="table-cell-custom">{reviewer}</td>
                          <td className="table-cell-custom text-center">
                            <div className="flex items-center justify-center gap-1">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-violet-600 hover:text-violet-800 data-[state=open]:bg-violet-50">
                                    ประเมินผล
                                    <ChevronDown className="w-4 h-4 ml-1" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleOpenDecisionModal(sub)}>
                                    <Edit className="w-4 h-4 mr-2 text-blue-500"/>ตัดสินผลงาน
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleViewDetails(sub)}>
                                    <Eye className="w-4 h-4 mr-2 text-gray-500"/>ดูรายละเอียด
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleDownloadFile(sub)}>
                                    <FileText className="w-4 h-4 mr-2 text-green-500"/>ดาวน์โหลดไฟล์
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleContactSubmitter(sub)}>
                                    <Users className="w-4 h-4 mr-2 text-purple-500"/>ติดต่อผู้ส่ง
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(sub);
                                }}
                                title="ลบผลงาน"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {filteredSubmissions.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-lg">ไม่พบผลงานที่ตรงกับเงื่อนไข</p>
                  <p className="text-sm">ลองปรับคำค้นหาหรือตัวกรองของคุณ</p>
                </div>
              )}

              <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-sm text-gray-600 text-center sm:text-left">
                  แสดง {filteredSubmissions.length} จาก {submissions.length} รายการ
                </p>
                <div className="flex items-center justify-center space-x-2">
                  <Button variant="outline" size="sm" onClick={() => handleFeatureClick('pagination')}>ก่อนหน้า</Button>
                  <Button variant="outline" size="sm" onClick={() => handleFeatureClick('pagination')}>ถัดไป</Button>
                </div>
              </div>
            </>
          )}
        </motion.div>

        <AlertDialog open={isDecisionModalOpen} onOpenChange={setIsDecisionModalOpen}>
          <AlertDialogContent className="sm:max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>ตัดสินผลงาน: {selectedSubmission?.title || selectedSubmission?.work_title}</AlertDialogTitle>
              <AlertDialogDescription>
                กรอกผลการประเมินและให้คะแนน (ถ้ามี)
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <Label htmlFor="decision" className="text-gray-700 font-medium">ผลการตัดสิน</Label>
                <Select 
                  value={decisionData.decision} 
                  onValueChange={(value) => setDecisionData(prev => ({...prev, decision: value}))}
                >
                  <SelectTrigger id="decision" className="w-full mt-1">
                    <SelectValue placeholder="เลือกผลการตัดสิน" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accepted">ตอบรับ (Accepted)</SelectItem>
                    <SelectItem value="conditional_accept">ตอบรับแบบมีเงื่อนไข (Conditional Accept)</SelectItem>
                    <SelectItem value="rejected">ปฏิเสธ (Rejected)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {decisionData.decision === 'conditional_accept' && (
                <div>
                  <Label htmlFor="conditions" className="text-gray-700 font-medium">เงื่อนไขการตอบรับ</Label>
                  <Textarea 
                    id="conditions" 
                    placeholder="ระบุเงื่อนไขที่ผู้ส่งผลงานต้องแก้ไข..." 
                    value={decisionData.conditions}
                    onChange={(e) => setDecisionData(prev => ({...prev, conditions: e.target.value}))}
                    className="mt-1 min-h-[80px]"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="reviewer" className="text-gray-700 font-medium">ผู้ประเมิน</Label>
                <Input 
                  id="reviewer" 
                  type="text" 
                  placeholder="ชื่อผู้ประเมิน เช่น อ.ดร.วิชัย ประเสริฐ" 
                  value={decisionData.reviewer_name}
                  onChange={(e) => setDecisionData(prev => ({...prev, reviewer_name: e.target.value}))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="score" className="text-gray-700 font-medium">คะแนน (ถ้ามี)</Label>
                <Input 
                  id="score" 
                  type="number" 
                  placeholder="0-100" 
                  value={decisionData.score}
                  onChange={(e) => setDecisionData(prev => ({...prev, score: e.target.value}))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="comments" className="text-gray-700 font-medium">ความคิดเห็นเพิ่มเติมถึงผู้ส่งผลงาน</Label>
                <Textarea 
                  id="comments" 
                  placeholder="ข้อเสนอแนะหรือความคิดเห็น..." 
                  value={decisionData.comments}
                  onChange={(e) => setDecisionData(prev => ({...prev, comments: e.target.value}))}
                  className="mt-1 min-h-[100px]"
                />
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setIsDecisionModalOpen(false)}>ยกเลิก</AlertDialogCancel>
              <AlertDialogAction onClick={handleDecisionSubmit} className="bg-violet-600 hover:bg-violet-700">บันทึกผล</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <SubmissionDetailModal 
          isOpen={isDetailModalOpen}
          onOpenChange={setIsDetailModalOpen}
          submission={selectedSubmission}
        />
        
        <ContactSubmitterModal
          isOpen={isContactModalOpen}
          onOpenChange={setIsContactModalOpen}
          submission={selectedSubmission}
          onSubmit={handleContactSubmit}
        />

        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>ยืนยันการลบผลงาน</AlertDialogTitle>
              <AlertDialogDescription>
                คุณต้องการลบผลงาน "{deleteTarget?.project_title || deleteTarget?.title || 'ไม่ระบุชื่อ'}" ใช่หรือไม่? การดำเนินการนี้ไม่สามารถย้อนกลับได้
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>ยกเลิก</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteConfirm();
                }}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? 'กำลังลบ...' : 'ลบ'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
};

export default SubmissionsReviewPage;
